import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ExternalLink, Sparkles, Users, Layers, ShoppingBag, Smartphone, ArrowRight } from 'lucide-react';
import LogoIcon from './icons/LogoIcon';
import { t } from '../translations';

declare global {
  interface Window {
    dataLayer?: any[];
  }
}

interface WebAppItem {
  name: string;
  subtitle: string;
  href: string;
  badge: string;
  badgeClass: string;
  iconBg: string;
  iconColor: string;
  icon: React.ReactNode;
}

const Header: React.FC<{ isHomePage?: boolean }> = ({ isHomePage = true }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [activeDropdown, setActiveDropdown] = useState<'services' | 'webApps' | 'mobileApps' | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<{ services: boolean; webApps: boolean; mobileApps: boolean }>({
    services: true,
    webApps: false,
    mobileApps: false
  });
  
  const navContainerRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const serviceItems = [
    { name: 'All Services', href: '/services/', desc: 'Performance marketing overview', highlight: true },
    { name: 'Google Ads & Shopping', href: '/services/google-ads/', desc: 'Shopping, Search & Performance Max' },
    { name: 'Meta Ads (Facebook & Instagram)', href: '/services/meta-ads/', desc: 'Advantage+ & full-funnel acquisition' },
    { name: 'TikTok Ads', href: '/services/tiktok-ads/', desc: 'Platform-native video & Spark Ads' },
    { name: 'Microsoft (Bing) Ads', href: '/services/microsoft-ads/', desc: 'High-intent desktop search traffic' },
    { name: 'Store Development & CRO', href: '/services/ecommerce-store-development/', desc: 'Shopify, WooCommerce & speed' },
    { name: 'Email Marketing & Klaviyo', href: '/services/email-marketing/', desc: 'Automated retention & win-back flows' },
  ];

  const webApps: WebAppItem[] = [
    {
      name: '82 Studio',
      subtitle: 'AI Video & Image Creation',
      href: 'https://studio.82marketing.com',
      badge: 'AI',
      badgeClass: 'bg-purple-100 text-purple-700',
      iconBg: 'bg-purple-50 group-hover:bg-purple-100 text-purple-600',
      iconColor: 'text-purple-600',
      icon: <Sparkles className="w-4 h-4" />
    },
    {
      name: '82 Leads',
      subtitle: 'B2B Lead Generation & Intelligence',
      href: 'https://82leads.com/',
      badge: 'Leads',
      badgeClass: 'bg-blue-100 text-blue-700',
      iconBg: 'bg-blue-50 group-hover:bg-blue-100 text-blue-600',
      iconColor: 'text-blue-600',
      icon: <Users className="w-4 h-4" />
    },
    {
      name: 'FlightDeck',
      subtitle: 'Marketing Ops & Cockpit',
      href: 'https://flightdeck.boo',
      badge: 'App',
      badgeClass: 'bg-indigo-100 text-indigo-700',
      iconBg: 'bg-indigo-50 group-hover:bg-indigo-100 text-indigo-600',
      iconColor: 'text-indigo-600',
      icon: <Layers className="w-4 h-4" />
    },
    {
      name: 'CataBoost',
      subtitle: 'Product Feed Management',
      href: 'https://cataboost.com',
      badge: 'Feed',
      badgeClass: 'bg-emerald-100 text-emerald-700',
      iconBg: 'bg-emerald-50 group-hover:bg-emerald-100 text-emerald-600',
      iconColor: 'text-emerald-600',
      icon: <ShoppingBag className="w-4 h-4" />
    }
  ];

  const mobileApps = [
    {
      name: 'Mazarion',
      subtitle: 'Holistic Self-Discovery Platform',
      href: 'https://mazarion.com/',
      badge: 'Mobile App',
      badgeClass: 'bg-amber-100 text-amber-800',
      iconBg: 'bg-amber-50 group-hover:bg-amber-100 text-amber-600',
      iconColor: 'text-amber-600',
      icon: <Smartphone className="w-4 h-4" />
    }
  ];

  const navLinks = [
    { href: '/case-studies/', label: t.header.caseStudies, sectionId: 'case-studies' },
    { href: '/about/', label: t.header.about, sectionId: 'about' },
    { href: '/blog/', label: 'Blog', sectionId: null },
    { href: '/contact/', label: t.header.contact, sectionId: 'contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navContainerRef.current && !navContainerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!isHomePage) return;
    const sectionIds = ['home', 'services', 'case-studies', 'about', 'testimonials', 'faq', 'contact'];
    const sections = sectionIds.map(id => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );
    sections.forEach(section => observer.observe(section));
    return () => sections.forEach(section => observer.unobserve(section));
  }, [isHomePage]);

  const handleDropdownEnter = (menu: 'services' | 'webApps' | 'mobileApps') => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(menu);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, label: string, sectionId?: string | null) => {
    window.dataLayer?.push({
      event: 'navigation_click',
      link_text: label,
      link_url: href,
      link_location: 'header_nav'
    });

    if (isHomePage && sectionId) {
      const targetElement = document.getElementById(sectionId);
      if (targetElement) {
        e.preventDefault();
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  const handleOutboundAppClick = (name: string, url: string, category: 'web_apps' | 'mobile_apps') => {
    window.dataLayer?.push({
      event: 'outbound_link_click',
      link_text: name,
      link_url: url,
      link_category: category,
      link_location: 'header_dropdown'
    });
    setActiveDropdown(null);
    setIsMenuOpen(false);
  };

  const handleCTAClick = () => {
    window.dataLayer?.push({
      event: 'cta_click',
      cta_text: t.header.cta,
      cta_location: 'header_main'
    });
  };

  return (
    <>
      <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-200' : 'bg-transparent'}`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a 
              href="/" 
              onClick={(e) => {
                if (isHomePage) {
                  e.preventDefault();
                  document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
                }
                handleNavClick(e, '/', 'Logo', 'home');
              }} 
              className="flex items-center gap-2 relative z-50 transition-transform hover:scale-105"
              aria-label="82 Marketing home"
            >
              <LogoIcon className="text-primary-500 h-12 w-12" />
            </a>

            {/* Desktop Navigation */}
            <nav ref={navContainerRef} className="hidden xl:flex items-center space-x-6">
              {/* Services Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => handleDropdownEnter('services')}
                onMouseLeave={handleDropdownLeave}
              >
                <div className="flex items-center">
                  <a
                    href="/services/"
                    onClick={(e) => handleNavClick(e, '/services/', t.header.services, 'services')}
                    className={`py-2 transition-colors flex items-center gap-1 text-[13px] font-medium ${
                      activeSection === 'services' || activeDropdown === 'services'
                        ? 'text-primary-600 font-semibold'
                        : 'text-gray-600 hover:text-primary-600'
                    }`}
                  >
                    <span>{t.header.services}</span>
                  </a>
                  <button
                    type="button"
                    onClick={() => setActiveDropdown(activeDropdown === 'services' ? null : 'services')}
                    className="p-1 text-gray-400 hover:text-primary-600 transition-colors focus:outline-none"
                    aria-expanded={activeDropdown === 'services'}
                    aria-label="Toggle Services Menu"
                  >
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${activeDropdown === 'services' ? 'rotate-180 text-primary-600' : 'text-gray-400'}`} />
                  </button>
                </div>

                {activeDropdown === 'services' && (
                  <div className="absolute top-full left-0 pt-2 w-88 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                    <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-2 overflow-hidden ring-1 ring-black/5 w-80">
                      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                        <span className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase">Our Services</span>
                        <span className="text-[10px] text-gray-400 font-normal">PPC & Growth</span>
                      </div>
                      <div className="py-1 flex flex-col gap-0.5">
                        {serviceItems.map((item) => (
                          <a
                            key={item.href}
                            href={item.href}
                            onClick={(e) => handleNavClick(e, item.href, item.name, null)}
                            className={`flex flex-col p-2.5 rounded-lg transition-colors group ${
                              item.highlight ? 'bg-primary-50/60 hover:bg-primary-50 border border-primary-100/80 mb-1' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className={`text-sm font-semibold transition-colors ${item.highlight ? 'text-primary-700' : 'text-gray-900 group-hover:text-primary-600'}`}>
                                {item.name}
                              </span>
                              <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all" />
                            </div>
                            <span className="text-xs text-gray-500 leading-tight mt-0.5">
                              {item.desc}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Web Apps Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => handleDropdownEnter('webApps')}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  type="button"
                  onClick={() => setActiveDropdown(activeDropdown === 'webApps' ? null : 'webApps')}
                  className={`py-2 transition-colors flex items-center gap-1.5 text-[13px] font-medium ${activeDropdown === 'webApps' ? 'text-primary-600 font-semibold' : 'text-gray-600 hover:text-primary-600'}`}
                  aria-expanded={activeDropdown === 'webApps'}
                >
                  <span>{t.header.webApps}</span>
                  <span className="text-[9px] bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded font-bold uppercase">4</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${activeDropdown === 'webApps' ? 'rotate-180 text-primary-600' : 'text-gray-400'}`} />
                </button>

                {activeDropdown === 'webApps' && (
                  <div className="absolute top-full left-0 pt-2 w-80 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                    <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-2 overflow-hidden ring-1 ring-black/5">
                      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                        <span className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase">Web Applications</span>
                        <span className="text-[10px] text-gray-400 font-normal">SaaS & Tools</span>
                      </div>
                      <div className="py-1 flex flex-col gap-0.5">
                        {webApps.map((app) => (
                          <a
                            key={app.href}
                            href={app.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => handleOutboundAppClick(app.name, app.href, 'web_apps')}
                            className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors group"
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-colors ${app.iconBg}`}>
                              {app.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                                  {app.name}
                                </span>
                                <span className={`text-[9px] px-1.5 py-0.2 rounded font-bold uppercase ${app.badgeClass}`}>
                                  {app.badge}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 leading-tight truncate mt-0.5">
                                {app.subtitle}
                              </p>
                            </div>
                            <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary-600 shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Apps Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => handleDropdownEnter('mobileApps')}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  type="button"
                  onClick={() => setActiveDropdown(activeDropdown === 'mobileApps' ? null : 'mobileApps')}
                  className={`py-2 transition-colors flex items-center gap-1.5 text-[13px] font-medium ${activeDropdown === 'mobileApps' ? 'text-primary-600 font-semibold' : 'text-gray-600 hover:text-primary-600'}`}
                  aria-expanded={activeDropdown === 'mobileApps'}
                >
                  <span>{t.header.mobileApps}</span>
                  <span className="text-[9px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold uppercase">1</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${activeDropdown === 'mobileApps' ? 'rotate-180 text-primary-600' : 'text-gray-400'}`} />
                </button>

                {activeDropdown === 'mobileApps' && (
                  <div className="absolute top-full left-0 pt-2 w-80 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                    <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-2 overflow-hidden ring-1 ring-black/5">
                      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                        <span className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase">Mobile Applications</span>
                        <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium">iOS / Android</span>
                      </div>
                      <div className="py-1">
                        {mobileApps.map((app) => (
                          <a
                            key={app.href}
                            href={app.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => handleOutboundAppClick(app.name, app.href, 'mobile_apps')}
                            className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors group"
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-colors ${app.iconBg}`}>
                              {app.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                                  {app.name}
                                </span>
                                <span className={`text-[9px] px-1.5 py-0.2 rounded font-bold uppercase ${app.badgeClass}`}>
                                  {app.badge}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 leading-tight truncate mt-0.5">
                                {app.subtitle}
                              </p>
                            </div>
                            <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary-600 shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </a>
                        ))}
                      </div>
                      <div className="pt-2 pb-1 px-3 border-t border-gray-100">
                        <a
                          href="https://mazarion.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleOutboundAppClick('Mazarion', 'https://mazarion.com/', 'mobile_apps')}
                          className="text-xs text-primary-600 font-medium hover:text-primary-700 flex items-center justify-between"
                        >
                          <span>Explore Mazarion Platform</span>
                          <span aria-hidden="true">&rarr;</span>
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Case Studies, About, Blog, Contact Links */}
              {navLinks.map((link) => (
                <a 
                  key={link.href} 
                  href={link.href} 
                  onClick={(e) => handleNavClick(e, link.href, link.label, link.sectionId)} 
                  className={`relative py-2 transition-colors text-[13px] ${
                    link.sectionId && activeSection === link.sectionId
                      ? 'text-primary-600 font-semibold'
                      : 'text-gray-600 hover:text-primary-600 font-medium'
                  }`}
                >
                  <span>{link.label}</span>
                  {link.sectionId && (
                    <span className={`absolute bottom-0 left-0 h-[2px] w-full bg-primary-500 transform transition-transform duration-300 ${activeSection === link.sectionId ? 'scale-x-100' : 'scale-x-0'}`}></span>
                  )}
                </a>
              ))}
            </nav>

            {/* Header CTA */}
            <div className="hidden xl:flex items-center">
              <a 
                href="/contact/" 
                onClick={(e) => { handleCTAClick(); handleNavClick(e, '/contact/', t.header.cta, 'contact'); }} 
                className="bg-primary-600 text-white font-semibold px-6 py-2.5 rounded-md hover:bg-primary-700 transition-all shadow-md hover:shadow-lg text-sm"
              >
                {t.header.cta}
              </a>
            </div>

            {/* Mobile Hamburger Button */}
            <button 
              className="xl:hidden text-gray-800 p-2 z-50" 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle Navigation Menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M6 18L18 6M6 6l12 12" strokeWidth={2}/></svg>
              ) : (
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M4 6h16M4 12h16m-7 6h7" strokeWidth={2}/></svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      <div className={`fixed inset-0 z-40 bg-white transform transition-transform duration-500 xl:hidden ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full px-6 pt-24 pb-8 justify-between overflow-y-auto">
          <nav className="flex flex-col space-y-3">
            {/* Services Group */}
            <div className="py-2 border-b border-gray-100">
              <button 
                type="button"
                onClick={() => setMobileExpanded(prev => ({ ...prev, services: !prev.services }))}
                className="w-full flex items-center justify-between text-lg font-heading font-bold text-gray-900 mb-2"
              >
                <span>{t.header.services}</span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${mobileExpanded.services ? 'rotate-180' : ''}`} />
              </button>

              {mobileExpanded.services && (
                <div className="flex flex-col space-y-1.5 pl-2 border-l-2 border-primary-200 mb-2">
                  {serviceItems.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      onClick={(e) => handleNavClick(e, item.href, item.name, null)}
                      className={`py-1 text-sm transition-colors ${item.highlight ? 'font-bold text-primary-600' : 'text-gray-700 hover:text-primary-600'}`}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Web Apps Group */}
            <div className="py-2 border-b border-gray-100">
              <button 
                type="button"
                onClick={() => setMobileExpanded(prev => ({ ...prev, webApps: !prev.webApps }))}
                className="w-full flex items-center justify-between text-lg font-heading font-bold text-gray-900 mb-2"
              >
                <div className="flex items-center gap-2">
                  <span>{t.header.webApps}</span>
                  <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-semibold">4</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${mobileExpanded.webApps ? 'rotate-180' : ''}`} />
              </button>
              
              {mobileExpanded.webApps && (
                <div className="flex flex-col space-y-1.5 pl-2 border-l-2 border-primary-200 mb-1">
                  {webApps.map((app) => (
                    <a
                      key={app.href}
                      href={app.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleOutboundAppClick(app.name, app.href, 'web_apps')}
                      className="py-1.5 flex items-center justify-between text-gray-700 hover:text-primary-600 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-gray-800">{app.name}</span>
                        <span className="text-xs text-gray-400">· {app.subtitle}</span>
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Apps Group */}
            <div className="py-2 border-b border-gray-100">
              <button 
                type="button"
                onClick={() => setMobileExpanded(prev => ({ ...prev, mobileApps: !prev.mobileApps }))}
                className="w-full flex items-center justify-between text-lg font-heading font-bold text-gray-900 mb-2"
              >
                <div className="flex items-center gap-2">
                  <span>{t.header.mobileApps}</span>
                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold">1</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${mobileExpanded.mobileApps ? 'rotate-180' : ''}`} />
              </button>

              {mobileExpanded.mobileApps && (
                <div className="flex flex-col space-y-1.5 pl-2 border-l-2 border-amber-200 mb-1">
                  {mobileApps.map((app) => (
                    <a
                      key={app.href}
                      href={app.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleOutboundAppClick(app.name, app.href, 'mobile_apps')}
                      className="py-1.5 flex items-center justify-between text-gray-700 hover:text-primary-600 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-gray-800">{app.name}</span>
                        <span className="text-xs text-gray-400">· {app.subtitle}</span>
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Remaining sections */}
            {navLinks.map((link) => (
              <a 
                key={link.href} 
                href={link.href} 
                onClick={(e) => handleNavClick(e, link.href, link.label, link.sectionId)} 
                className="text-2xl font-heading font-bold text-gray-900 py-1"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex flex-col gap-4 mt-6">
            <a 
              href="/contact/" 
              onClick={(e) => { handleCTAClick(); handleNavClick(e, '/contact/', t.header.cta, 'contact'); }} 
              className="w-full bg-primary-600 text-white font-bold text-center py-3.5 rounded-xl shadow-md"
            >
              {t.header.cta}
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
